/**
 * SEO/GEO configuration for HandInvoice.
 *
 * Single source of truth for per-route metadata and JSON-LD structured
 * data. Consumed by `hooks/usePageMeta.js` (meta tags) and the public page
 * components (JSON-LD blocks).
 *
 * SITE_URL is the canonical production origin. It is used for absolute
 * canonical / og:url / og:image values; we deliberately do NOT derive
 * these from `window.location.origin`, because during prerendering that
 * would bake in the localhost address of the prerender server.
 */
import { TRADES } from "./data/trades";

export const SITE_URL = "https://handinvoice.com";

export const OG_IMAGE_PATH = "/og-image.png"; // PNG only — never SVG.
export const OG_IMAGE_URL = SITE_URL + OG_IMAGE_PATH;

/**
 * Public, indexable routes. Any path NOT present here is treated as
 * private → noindex (covers /review/*, /invoices, /invoices/:id, /settings,
 * /sign-in/*, /sign-up/*, /success/*, /audio-test).
 */
export const SEO = {
  "/": {
    title: "HandInvoice — voice-to-invoice for independent professionals",
    description:
      "Generate professional PDF invoices by speaking. No signup required. Built for plumbers, electricians, freelancers, contractors, and anyone who works for themselves.",
    keywords:
      "voice invoice, voice to invoice, invoice generator, free invoice maker, contractor invoice, plumber invoice, invoice by voice",
  },
  "/privacy": {
    title: "Privacy — HandInvoice",
    description:
      "How HandInvoice handles your data. We don't store audio recordings. We don't sell your data. Plain-language privacy policy.",
  },
  "/faq": {
    title: "FAQ — HandInvoice",
    description:
      "Common questions about HandInvoice: how it works, pricing, privacy, accuracy, supported devices, and more.",
  },
  // Trade landing pages (/plumbers, /electricians, …) — see data/trades.js.
  ...Object.fromEntries(
    TRADES.map((t) => [
      "/" + t.slug,
      { title: t.title, description: t.description, keywords: t.keywords },
    ]),
  ),
};

/** Strip a trailing slash (except for the root path). */
export function normalizePath(pathname) {
  if (!pathname) return "/";
  const p = pathname.replace(/\/+$/, "");
  return p === "" ? "/" : p;
}

/** Per-route SEO entry, or null for private/non-indexed routes. */
export function getSeo(pathname) {
  return SEO[normalizePath(pathname)] || null;
}

/** True for routes that must never be indexed. */
export function isPrivateRoute(pathname) {
  return getSeo(pathname) === null;
}

// ---------------------------------------------------------------------------
// JSON-LD structured data builders
// ---------------------------------------------------------------------------

/** SoftwareApplication schema for the home page. */
export function softwareApplicationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "HandInvoice",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android (via PWA)",
    description:
      "Voice-to-invoice tool for independent professionals. Speak naturally, generate a professional PDF invoice.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  };
}

/** HowTo schema for the home page. */
export function howToLd() {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to generate an invoice by voice",
    step: [
      {
        "@type": "HowToStep",
        name: "Set up your business",
        text: "Open Settings and add your business name, contact details, and license number.",
      },
      {
        "@type": "HowToStep",
        name: "Tap record and describe the job",
        text: "Tap the record button and speak naturally about the work you did, who it was for, hours, materials, and any tax.",
      },
      {
        "@type": "HowToStep",
        name: "Review and download",
        text: "Check the extracted details on the Review screen, make any corrections, and download your PDF invoice.",
      },
    ],
  };
}

/**
 * FAQPage schema, built dynamically from the FAQ data source so the schema
 * can never drift from the visible Q&A. Pass the array from data/faq.js.
 */
export function faqPageLd(questions) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (questions || []).map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

/** WebPage schema for the privacy page. */
export function webPageLd(pathname) {
  const seo = getSeo(pathname) || {};
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: seo.title || "HandInvoice",
    description: seo.description || "",
    url: SITE_URL + normalizePath(pathname),
    isPartOf: {
      "@type": "WebSite",
      name: "HandInvoice",
      url: SITE_URL + "/",
    },
  };
}
